// How a contact's uncertainty ring grows while nobody is looking at it (spec §7: the COP — common
// operating picture — is what blue believes, not what is true). A report is only accurate at the moment
// it was made; from then on the enemy could be anywhere inside a circle that widens at roughly the speed
// it could move, so the ring is dead reckoning drawn honestly rather than a fixed error bar that quietly
// lies as it ages.
//
// One helper, shared: the live overlay and the debrief replay must draw the same circle for the same
// contact, or the replay would be showing the player a picture they never actually had.

import type { Contact } from '../sim/types';

/** Contacts older than this are "last seen" ghosts (spec §7); their ring stops growing there. */
export const GHOST_AGE_MIN = 10;

/** No ring is ever drawn wider than this: past it the report says nothing useful about position. */
export const MAX_SPREAD_M = 3000;

/**
 * How fast the circle of possibility widens, by what the observer thought it was looking at. Fast
 * vehicles open the most ground per minute; dismounted infantry the least; an aircraft most of all.
 */
const SPREAD_M_PER_MIN: Record<Contact['typeConf'], number> = {
  armor: 300, mech: 300, recon: 300, vehicle: 300,
  inf: 80, unknown: 80,
  spArty: 150, mortar: 150, hq: 150, log: 150,
  uas: 600,
};

/** The per-minute growth rate for an observed type; the middle rate for anything unrecognised. */
export function spreadRateMPerMin(typeConf: Contact['typeConf']): number {
  return SPREAD_M_PER_MIN[typeConf] ?? 150;
}

/**
 * The radius, in meters, to draw for a contact: the reported position error plus the ground it could
 * have covered since, capped at `MAX_SPREAD_M`. Growth stops at `GHOST_AGE_MIN` — a ghost keeps the
 * ring it had when it went stale (and is drawn at ghost alpha), rather than swelling to fill the map.
 */
export function contactSpreadM(typeConf: Contact['typeConf'], ageMin: number, errorM: number): number {
  const age = Math.max(0, Math.min(ageMin, GHOST_AGE_MIN));
  return Math.min(MAX_SPREAD_M, errorM + age * spreadRateMPerMin(typeConf));
}

/**
 * How solid the ring looks: full strength on a fresh report, half by the time it goes stale. The fade
 * runs alongside the growth so age reads twice — bigger and quieter — before the contact becomes a ghost.
 */
export function contactRingAlpha(ageMin: number): number {
  const age = Math.max(0, Math.min(ageMin, GHOST_AGE_MIN));
  return 1 - 0.5 * (age / GHOST_AGE_MIN);
}
